import {
  Badge,
  Cushion,
  FlexContainer,
  Image,
  MaxWidth,
  Rectangle,
  Typography,
  withTheme,
} from '@pitchero/react-ui';

import { DateTime } from 'luxon';
import PropTypes from 'prop-types';
import React from 'react';
import styled from 'styled-components';
import CLUB_PROP_TYPES from '../../lib/prop-types/club';
import Form from './form';
import EventSummaries from './event-summaries';
import Navigation from './navigation';

const HeaderGrid = styled.div`
  display: grid;
  grid-template-columns: 60px minmax(0, 1fr) auto;
  grid-template-rows: 1fr auto 1fr auto;
  grid-template-areas:
    'homeLogo homeTitle homeScore'
    '. homeExtra .'
    'awayLogo awayTitle awayScore'
    '. awayExtra .';
  grid-gap: 10px;
  padding-bottom: 20px;

  @media (min-width: ${(props) => props.breakpoint}px) {
    grid-template-columns: 70px 1fr minmax(min-content, 1fr) 1fr 70px;
    grid-template-rows: 1fr auto;
    grid-template-areas:
      'homeLogo homeTitle scores awayTitle awayLogo'
      '. homeExtra . awayExtra .';
    grid-gap: 0px 40px;
    padding-bottom: 0px;
  }
`;

const MatchCentreHeader = ({
  activeSection,
  club,
  overview,
  teamId,
  teamName,
  fixtureId,
  formGuide,
  hasEvents,
  hasLineup,
  hasPhotos,
  hasScorecard,
  hasTable,
  hasReport,
  scoreCard,
  isMobile,
  uiTheme,
}) => {
  const hasEventsSummary =
    (overview.home.events && overview.home.events.length > 0) ||
    (overview.away.events && overview.away.events.length > 0);

  let hasForm = false;
  if (formGuide) {
    const { home: homeForm, away: awayForm } = formGuide;
    hasForm =
      homeForm && homeForm.length > 0 && awayForm && awayForm.length > 0 && !overview.outcome;
  }

  const renderLogo = ({ homeOrAway, colours, gridArea }) => (
    <FlexContainer justifyContent="center" alignItems="center" style={{ gridArea }}>
      {overview.ha === homeOrAway ? (
        <Image src={club.logo} alt={club.name} width={60} height={60} />
      ) : (
        <Badge primary={colours.primary} secondary={colours.secondary} size={50} />
      )}
    </FlexContainer>
  );

  const renderTeamContent = ({ homeOrAway, name, gridArea, events, teamForm }) => (
    <>
      <Typography
        preset="name"
        size={isMobile ? 16 : 24}
        lineHeight={isMobile ? '16px' : '24px'}
        style={{ gridArea, margin: 'auto 0', wordBreak: 'keep-all' }}
        color="#fff"
        align={!isMobile && homeOrAway === 'a' ? 'right' : 'left'}
      >
        {overview.ha === homeOrAway ? teamName : name}
      </Typography>
      {(hasEventsSummary || hasForm) && (
        <div
          style={{
            gridArea: homeOrAway === 'h' ? 'homeExtra' : 'awayExtra',
            textAlign: !isMobile && homeOrAway === 'a' ? 'right' : 'left',
            marginTop: isMobile ? '-25px' : '0',
          }}
        >
          {hasEventsSummary && (
            <EventSummaries sportId={overview.sportId} events={events} isMobile={isMobile} />
          )}
          {hasForm && <Form fixtures={teamForm} />}
        </div>
      )}
    </>
  );

  const renderScore = ({ score, homeOrAway, gridArea }) => {
    if (scoreCard && scoreCard.gameType === 'pairs') {
      const [homeInnings, awayInnings] = scoreCard.innings;
      if (homeInnings && awayInnings) {
        const homeNetScore =
          homeInnings.ha === 'h' ? homeInnings.teamNetScore : awayInnings.teamNetScore;
        const awayNetScore =
          awayInnings.ha === 'a' ? awayInnings.teamNetScore : homeInnings.teamNetScore;

        if (homeNetScore && awayNetScore) {
          return (
            <div
              style={{ gridArea, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
            >
              {score && (
                <Rectangle
                  fill="#000"
                  radius="rounded"
                  style={{ border: '1px solid #666', padding: '8px 14px', width: '100%' }}
                >
                  <FlexContainer
                    flexDirection="column"
                    justifyContent="center"
                    alignItems="center"
                    style={{ gap: '10px' }}
                  >
                    <Typography
                      preset="title"
                      color="#fff"
                      size={isMobile ? 20 : 26}
                      style={{
                        whiteSpace: 'nowrap',
                      }}
                    >
                      <span style={{ fontSize: isMobile ? 16 : 22 }}>Net:&nbsp;</span>
                      {score.split('/')[0]}
                    </Typography>
                    <Typography
                      preset="description"
                      color="#fff"
                      size={isMobile ? 14 : 16}
                      style={{
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {homeOrAway === 'h' ? homeNetScore : awayNetScore}
                    </Typography>
                  </FlexContainer>
                </Rectangle>
              )}
            </div>
          );
        }
      }
    }

    return (
      <div style={{ gridArea, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        {score && (
          <Rectangle
            fill="#000"
            radius="rounded"
            style={{ border: '1px solid #666', padding: '8px 14px', width: '100%' }}
          >
            <FlexContainer
              flexDirection="column"
              justifyContent="center"
              alignItems="center"
              style={{ gap: '10px' }}
            >
              <Typography
                preset="title"
                color="#fff"
                size={isMobile ? 20 : 26}
                style={{
                  whiteSpace: 'nowrap',
                }}
              >
                {score}
              </Typography>
            </FlexContainer>
          </Rectangle>
        )}
      </div>
    );
  };

  return (
    <>
      <Rectangle fill="#000" component="div">
        <MaxWidth maxWidth={1366} center>
          <Cushion all="medium" component="div">
            <FlexContainer flexDirection="column" style={{ gap: '20px' }}>
              <Typography
                size={isMobile ? 14 : 16}
                color="#fff"
                align={isMobile ? 'left' : 'center'}
              >
                {DateTime.fromISO(overview.date).toFormat('ccc dd LLL y')}
                {overview?.competition && (
                  <>
                    &nbsp; &middot; &nbsp;
                    {overview.competition}
                  </>
                )}
              </Typography>
              <HeaderGrid breakpoint={uiTheme?.breakpoints?.fullHeader || 820}>
                {renderLogo({
                  homeOrAway: 'h',
                  colours: overview.home.colours,
                  gridArea: 'homeLogo',
                })}
                {renderTeamContent({
                  homeOrAway: 'h',
                  name: overview.home.name,
                  gridArea: 'homeTitle',
                  events: overview.home.events,
                  teamForm: formGuide?.home,
                })}
                {isMobile &&
                  renderScore({
                    score: overview.home.score,
                    homeOrAway: 'h',
                    gridArea: 'homeScore',
                  })}
                {!isMobile && (
                  <>
                    {!overview.outcome ? (
                      <div
                        style={{
                          gridArea: 'scores',
                          display: 'flex',
                          justifyContent: 'center',
                          alignItems: 'center',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        <Typography preset="description" color="#fff" size={30}>
                          {overview.kickoff}
                        </Typography>
                      </div>
                    ) : (
                      <div style={{ display: 'table', margin: 'auto' }}>
                        <div
                          style={{
                            display: 'table-cell',
                            minWidth: '40%',
                            width: '40%',
                            paddingRight: '15px',
                          }}
                        >
                          {renderScore({ score: overview.home.score, homeOrAway: 'h' })}
                        </div>
                        <div
                          style={{
                            display: 'table-cell',
                            minWidth: '40%',
                            width: '40%',
                            paddingLeft: '15px',
                          }}
                        >
                          {renderScore({ score: overview.away.score, homeOrAway: 'a' })}
                        </div>
                      </div>
                    )}
                  </>
                )}
                {renderLogo({
                  homeOrAway: 'a',
                  colours: overview.away.colours,
                  gridArea: 'awayLogo',
                })}
                {renderTeamContent({
                  homeOrAway: 'a',
                  name: overview.away.name,
                  gridArea: 'awayTitle',
                  events: overview.away.events,
                  teamForm: formGuide?.away,
                })}
                {isMobile &&
                  renderScore({
                    score: overview.away.score,
                    homeOrAway: 'a',
                    gridArea: 'awayScore',
                  })}
              </HeaderGrid>
            </FlexContainer>
          </Cushion>
          <div>
            <Navigation
              teamId={teamId}
              fixtureId={fixtureId}
              hasEvents={hasEvents}
              hasLineup={hasLineup}
              hasReport={hasReport}
              hasPhotos={hasPhotos}
              hasScorecard={hasScorecard}
              hasTable={hasTable}
              activeSection={activeSection}
            />
          </div>
        </MaxWidth>
      </Rectangle>
    </>
  );
};

MatchCentreHeader.defaultProps = {
  formGuide: { form: {} },
  scoreCard: {},
};

MatchCentreHeader.propTypes = {
  activeSection: PropTypes.string.isRequired,
  club: CLUB_PROP_TYPES.isRequired,
  fixtureId: PropTypes.string.isRequired,
  formGuide: PropTypes.shape({
    away: PropTypes.arrayOf(PropTypes.shape()),
    home: PropTypes.arrayOf(PropTypes.shape()),
  }),
  hasEvents: PropTypes.bool.isRequired,
  hasLineup: PropTypes.bool.isRequired,
  hasReport: PropTypes.bool.isRequired,
  hasPhotos: PropTypes.bool.isRequired,
  hasScorecard: PropTypes.bool.isRequired,
  hasTable: PropTypes.bool.isRequired,
  overview: PropTypes.shape({
    away: PropTypes.shape({
      name: PropTypes.string.isRequired,
      colours: PropTypes.shape({
        primary: PropTypes.string.isRequired,
        secondary: PropTypes.string.isRequired,
      }),
      events: PropTypes.arrayOf(PropTypes.shape()),
      isCricket: PropTypes.bool,
      outcome: PropTypes.string,
      sportId: PropTypes.number,
      score: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    }).isRequired,
    competition: PropTypes.string.isRequired,
    elapsedPercent: PropTypes.number,
    date: PropTypes.string.isRequired,
    ha: PropTypes.oneOf(['h', 'a']).isRequired,
    home: PropTypes.shape({
      name: PropTypes.string.isRequired,
      colours: PropTypes.shape({
        primary: PropTypes.string.isRequired,
        secondary: PropTypes.string.isRequired,
      }),
      events: PropTypes.arrayOf(PropTypes.shape()),
      score: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    }).isRequired,
    hasOutcome: PropTypes.bool.isRequired,
    kickoff: PropTypes.string,
    instructions: PropTypes.string,
    isCricket: PropTypes.bool,
    isLive: PropTypes.bool.isRequired,
    outcome: PropTypes.string,
    typeShort: PropTypes.string.isRequired,
    sportId: PropTypes.number,
  }).isRequired,
  scoreCard: PropTypes.arrayOf(
    PropTypes.shape({
      gameType: PropTypes.string,
      innings: PropTypes.arrayOf(
        PropTypes.shape({
          batting: PropTypes.arrayOf(PropTypes.shape()),
          bowlers: PropTypes.arrayOf(PropTypes.shape()),
          fow: PropTypes.arrayOf(PropTypes.shape()),
          ha: PropTypes.oneOf(['h', 'a']).isRequired,
          teamNetScore: PropTypes.number,
        }),
      ),
      isPlayCricket: PropTypes.bool,
      toss: PropTypes.string,
    }),
  ),
  teamId: PropTypes.number.isRequired,
  teamName: PropTypes.string.isRequired,
  isMobile: PropTypes.bool.isRequired,
  uiTheme: PropTypes.shape().isRequired,
};

export default withTheme(MatchCentreHeader);
